import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

type Role = "admin" | "auctioneer" | "clerk" | "bidder";

const ROLE_ORDER: Role[] = ["bidder", "clerk", "auctioneer", "admin"];

export const useUserRole = () => {
  const { user } = useAuth();
  const [isAuctioneer, setIsAuctioneer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isClerk, setIsClerk] = useState(false);
  const [isBidder, setIsBidder] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkRoles = async () => {
      if (!user) {
        setIsAuctioneer(false);
        setIsAdmin(false);
        setIsClerk(false);
        setIsBidder(false);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);

        if (error) throw error;

        const roles = data?.map(r => r.role) || [];
        setIsAuctioneer(roles.includes("auctioneer"));
        setIsAdmin(roles.includes("admin"));
        setIsClerk(roles.includes("clerk"));
        setIsBidder(roles.includes("bidder"));
      } catch (error) {
        console.error("Error checking user roles:", error);
      } finally {
        setLoading(false);
      }
    };

    checkRoles();
  }, [user]);

  const isStaff = isAdmin || isAuctioneer || isClerk;

  // Hierarchy check: admin >= auctioneer >= clerk >= bidder.
  // Flat flags above stay exact-role; this is for "is at least role X".
  const userRank = isAdmin
    ? ROLE_ORDER.indexOf("admin")
    : isAuctioneer
      ? ROLE_ORDER.indexOf("auctioneer")
      : isClerk
        ? ROLE_ORDER.indexOf("clerk")
        : isBidder
          ? ROLE_ORDER.indexOf("bidder")
          : -1;

  const isAtLeast = (role: Role) => userRank >= ROLE_ORDER.indexOf(role);

  return { isAuctioneer, isAdmin, isClerk, isBidder, isStaff, isAtLeast, loading };
};
